// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// Third party imports
import { cloneDeep } from 'lodash';
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// Constant imports
import { StringFormatConstants } from '../../../../../_constants/string-format-constants';
import { ActionConstants } from '../../../../../_constants/action-constants';
import { MessageInterval } from '../../../../../_constants/messages-intervals';
import { ARConstants } from '../../../../cirabooks-module/_constants/cb-ar-constants';
import { URLConstants } from '../../../../../_constants/url-constants';

// Service imports
import { HelperService } from '../../../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../../../_services/shared-services/server-error.service';
import { ARAdjustmentService } from '../../../_services/account-receivable/ar-adjustment.service';
import { UserService } from '../../../../../_services/data-services/user.service';
import { CirabooksHelperService } from '../../../../_services/cirabooks-helper.service';

// Model imports
import { ARAdjustmentRawDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-raw-dto';
import { KeyValueResultDto } from '../../../../../_models/key-value-result-dto';
import { ARAdjustmentEditorResultDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-editor-result-dto';
import { ARAdjustmentParameterDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-parameter-dto';
import { ARAdjustmentItemRawDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-item-raw-dto';
import { DimensionSelectorResultDto } from '../../../_models/common/dimensions/dimension-selector-result-dto';
import { ARAdjustmentEditorParameterDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-editor-parameter-dto';
import { GLAccountSelectorResultDto } from '../../../_models/common/gl-account/glaccount-selector-result-dto';
import { ARAdjustmentGLAccountLabelsParameterDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-gl-account-labels-parameter-dto';
import { GLAccountLabelSelectorResultDto } from '../../../_models/common/gl-account/gl-account-label-selector-result-dto';
import { DimensionOptionCodeRawDto } from '../../../_models/common/dimensions/dimension-option-code-raw-dto';
import { TransactionLogParameterDto } from '../../../_models/common/transaction-log-parameter-dto';
import { TransactionLogResultDto } from '../../../_models/common/transaction-log-result-dto';
import { ARAdjustmentAttachmentRawDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-attachment-raw-dto';
import { SubCategoryByGlAccountParametersDto } from '../../../_models/common/subcategory-by-gl-account-parametere-dto';
import { GLSubcategorySelectorResultDto } from '../../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { AccountSelectorInfo } from '../../../_models/common/gl-account/account-selector-info';
import { AttachmentRawDto } from '../../../../../management-common/_models/attachment-raw-dto';

@Component({
    selector: 'app-ar-adjustment-editor',
    templateUrl: './ar-adjustment-editor.component.html',
    styleUrls: ['./ar-adjustment-editor.component.scss']
})
export class ARAdjustmentEditorComponent implements OnInit {
    @ViewChild('arAdjustmentEditorGrid', { static: false }) arAdjustmentEditorGrid: DxDataGridComponent;
    @ViewChild('arAdjustmentMessageBox', { static: false }) arAdjustmentMessageBox: MessageBoxComponent;
    @ViewChild('arAdjustmentEditorPopup', { static: false }) arAdjustmentEditorPopup: DxPopupComponent;

    @Input() visible: boolean;
    @Input() popupTitle: string;
    @Input() isEdited = false;
    @Input() isLimitedEdit = false;
    @Input() customerId: number;
    @Input() arAdjustmentParam: ARAdjustmentParameterDto;
    @Input() customerCode: string; // Holds R number. Input coming from Property Context => Statements.

    @Output() onEditorClose = new EventEmitter<boolean>();

    isInternalUser = false;
    loadingVisible = false;

    adjustmentTypes: KeyValueResultDto[] = [];
    arAdjustmentEditor: ARAdjustmentEditorResultDto = new ARAdjustmentEditorResultDto();
    adjustmentInfo: ARAdjustmentRawDto = new ARAdjustmentRawDto();
    originalAdjusment: ARAdjustmentRawDto;
    subLedgers: KeyValueResultDto[] = [];
    modifiedARAdjustment: ARAdjustmentRawDto = new ARAdjustmentRawDto();
    dimensionSource: DimensionSelectorResultDto[] = [];
    glAccountNumberSource: GLAccountSelectorResultDto[] = [];
    glAccountLabelSource: GLAccountLabelSelectorResultDto[] = [];
    arAdjustmentAttachments: Array<AttachmentRawDto> = [];

    accountTypeSource: any[] = [];
    selectedAccountType: string;

    addLineItemButtonOptions = '';
    currencyFormat = StringFormatConstants.currencyFormat;
    transactionLogTitle: string;
    displayTransactionLogPopUp = false;
    transactionLogResultData: TransactionLogResultDto[] = [];

    iconCancel = IconConstants.CANCEL;
    iconSave = IconConstants.SAVE;
    iconKey = IconConstants.KEY;
    iconHistory = IconConstants.HISTORY;
    iconDelete = IconConstants.DELETE;
    dateFormat = StringFormatConstants.dateFormat;

    apiUrl = 'api/cb/ar/adjustment';
    showManualCreditApplication: boolean;

    constructor(
        private readonly helperService: HelperService,
        private readonly arAdjustmentService: ARAdjustmentService,
        private readonly serverErrorService: ServerErrorService,
        private readonly userService: UserService,
        private readonly cirabooksHelperService: CirabooksHelperService
    ) {
        this.isInternalUser = this.userService.isInternal();

        this.onNewLineItemClick = this.onNewLineItemClick.bind(this);
        this.getDefaultOffsetGLAccountNumber = this.getDefaultOffsetGLAccountNumber.bind(this);
        this.isOffsetGLAccountDisabled = this.isOffsetGLAccountDisabled.bind(this);
    }

    ngOnInit() {
        this.addLineItemButtonOptions = JSON.stringify({
            beforeIcons: [IconConstants.ADD],
            label: 'New Line Item'
        });

        this.configureAccountTypeSource();
        if (!!this.arAdjustmentParam) {
            this.populateARadjustmentEditor();
        }
    }

    onShown(): void {
        this.arAdjustmentEditorPopup.instance.repaint();
        if (!!this.arAdjustmentEditorGrid && !!this.arAdjustmentEditorGrid.instance) {
            this.arAdjustmentEditorGrid.instance.repaint();
        }
    }

    onHidden(): void {
        this.visible = false;
        this.onEditorClose.emit(false);
    }

    onSubLedgerChanged(event): void {
        if (!!event && !!event.value) {
            this.populateGLAccountsLabel(event.value);
        }
    }

    onCustomerNameClick(data): void {
        if (this.arAdjustmentParam.communityId) {
            this.helperService.openDefaultPropertyPageInNewWindow(
                this.arAdjustmentParam.communityId,
                data.customerCode
            );
        }
    }

    onNewLineItemClick(): void {
        this.addRawLineItem();
    }

    onAccountSelection(event: AccountSelectorInfo, cellInfo: any) {
        this.selectedAccountType = event.accountType;

        cellInfo.setValue(event.title);
        cellInfo.data.accountLabelCode = event.accountLabelCode;
        cellInfo.data.glAccountNumber = event.glAccountNumber;
        cellInfo.data.offsetGLAccountNumber = event.offsetGLAccountNumber;
        cellInfo.data.subCategoryCode = event.subCategoryCode;
        cellInfo.data.tab = event.accountType;
        cellInfo.data.disableSubCategory = event.disableSubCategory;
        cellInfo.data.isAccountSubcategoryValid = true;
        cellInfo.data.accountSubcategoryValidationMessage = null;
        if (this.arAdjustmentEditor.entityUsesSubCategory && event.glAccountNumber) {
            this.populateSubcategories(event.glAccountNumber, cellInfo.data, null, event.accountType, false, true);
        }
    }

    onTransactionLogClick(): void {
        if (!!this.arAdjustmentParam.adjustmentId) {
            this.transactionLogTitle = 'Log of AR Adjustment ' + this.arAdjustmentParam.adjustmentId;
            this.populateTransactionLog();
            this.displayTransactionLogPopUp = true;
        }
    }

    onEditorContentReady(event): void {
        event.component.filter(['crudAction', '<>', ActionConstants.DELETE]);
    }

    onAttachmentUpdate(attachments: any[]): void {
        const arAdjustmentAttachments: ARAdjustmentAttachmentRawDto[] = [];
        attachments.forEach((attachment) => {
            let obj: ARAdjustmentAttachmentRawDto = new ARAdjustmentAttachmentRawDto();
            obj = Object.assign(obj, attachment);
            obj.crudAction = attachment.action;
            obj.fileDataBase64 = attachment.fileBase64String;
            delete obj['action'];
            delete obj['fileBase64String'];
            delete obj['customerId'];
            if (Utils.notNullAndDefined(obj.crudAction)) {
                arAdjustmentAttachments.push(obj);
            }
        });

        this.adjustmentInfo.attachments = arAdjustmentAttachments;
    }

    onDeleteFromGrid(item: ARAdjustmentItemRawDto): void {
        if (!item.arAdjustmentItemId) {
            this.adjustmentInfo.items = this.adjustmentInfo.items.filter((d) =>
                Utils.notEquals(d['sequenceItemId'], item['sequenceItemId'])
            );
        } else {
            this.adjustmentInfo.items.forEach((ele) => {
                if (Utils.equals(ele.sequenceItemId, item.sequenceItemId)) {
                    ele.crudAction = ActionConstants.DELETE;
                }
            });
        }

        this.arAdjustmentEditorGrid.instance.filter(['crudAction', '<>', ActionConstants.DELETE]);
        this.arAdjustmentEditorGrid.instance.refresh();
    }

    onRowUpdated(event): void {
        const rowData = event.data;
        const dimensions: DimensionOptionCodeRawDto[] = [];

        if (Utils.isNullOrEmpty(rowData.crudAction)) {
            rowData.crudAction = ActionConstants.EDIT;
        }

        if (Utils.isTrue(this.arAdjustmentEditor.allowFullEdit)) {
            rowData.openBalance = rowData.amount;
        }

        this.adjustmentInfo.items.forEach((item) => {
            if (Utils.equals(item.sequenceItemId, rowData.itemId)) {
                this.dimensionSource.forEach((row) => {
                    const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                    obj.dimensionCode = row.code;
                    obj.dimensionOptionCode = rowData[row.code];
                    dimensions.push(obj);
                });
                item.dimensions = dimensions;
            }
        });
    }

    onSubcateogryChanged(event: any, cellInfo: any): void {
        setTimeout(() => {
            cellInfo.setValue(event.value);
        }, 500);
    }

    onSaveClick(): void {
        this.arAdjustmentEditorGrid.instance.saveEditData().then(() => {
            const isLineItemPrepared = this.prepareLineItems();
            if (Utils.isTrue(isLineItemPrepared)) {
                this.saveAdjustment();
            }
        });
    }

    saveAdjustment(): void {
        const formStatus = this.checkValidity();
        const resultStatus = this.checkLineItemValidity();
        if (Utils.isFalse(formStatus.isValid)) {
            this.arAdjustmentMessageBox.showErrorMessage('Error', formStatus.message);
        } else if (Utils.isFalse(resultStatus.isValid)) {
            this.arAdjustmentMessageBox.showErrorMessage('Error', resultStatus.message);
            this.resetARAdjustmentRawItem();
        } else {
            this.modifiedARAdjustment.customerId = this.customerId;
            this.modifiedARAdjustment.communityId = this.arAdjustmentParam.communityId;
            this.modifiedARAdjustment.managementCompanyId = this.arAdjustmentParam.managementCompanyId;
            // In case of DELETE crud Action following code is required to remove null dimensionOptionCode
            this.modifiedARAdjustment.items.forEach((item) => {
                item.dimensions = item.dimensions.filter((d) => Utils.notNullOrEmpty(d.dimensionOptionCode));
            });
            if (!this.isEdited) {
                this.addARAdjustment();
            } else {
                this.editARAdjustment();
            }
        }
    }

    getDefaultOffsetGLAccountNumber(): string {
        if (Utils.notNullAndDefined(this.adjustmentInfo) && Utils.notNullAndDefined(this.arAdjustmentEditor)) {
            return Utils.equalsIgnoreCase(this.adjustmentInfo.adjustmentTypeCode, ARConstants.CREDIT_CODE)
                ? this.arAdjustmentEditor.defaultCROffsetGLAccountNumber
                : this.arAdjustmentEditor.defaultDROffsetGLAccountNumber;
        } else {
            return null;
        }
    }

    isOffsetGLAccountDisabled(): boolean {
        return Utils.equalsIgnoreCase(this.adjustmentInfo.adjustmentTypeCode, ARConstants.CREDIT_CODE);
    }

    showARManualCreditApplication(): void {
        this.showManualCreditApplication = true;
    }

    onManualCAEditorClose(refresh: boolean): void {
        this.showManualCreditApplication = false;
        if (Utils.isTrue(refresh)) {
            this.onEditorClose.emit(true);
        }
    }

    private checkValidity(): any {
        const result = { isValid: true, message: null };

        if (!this.modifiedARAdjustment.adjustmentTypeCode) {
            result.isValid = false;
            result.message = 'Adjustment Type is required';
        }
        if (!this.modifiedARAdjustment.description) {
            result.isValid = false;
            result.message = 'Description is required.';
        }
        if (!this.modifiedARAdjustment.docDate) {
            result.isValid = false;
            result.message = 'Document Date is required.';
        }
        if (!this.modifiedARAdjustment.postDate) {
            result.isValid = false;
            result.message = 'Post Date is required.';
        }
        if (!this.modifiedARAdjustment.subLedgerId) {
            result.isValid = false;
            result.message = 'Sub Ledger is required';
        }

        return result;
    }

    private showSuccessMessage(message: string): void {
        notify(message, 'Success', MessageInterval.successMsgInterval);
    }

    private editARAdjustment(): void {
        this.loadingVisible = true;

        // Note: As per discussion with Vinay on 28/06/2024, pass attachment with crudAction available. We should avoid sending those which crudAction is empty/null.
        this.modifiedARAdjustment.attachments = this.modifiedARAdjustment.attachments.filter((d) =>
            Utils.notNullOrEmpty(d.crudAction)
        );
        this.arAdjustmentService.editARAdjustment(this.modifiedARAdjustment).subscribe(
            () => {
                this.showSuccessMessage('AR Adjustment updated successfully.');
                this.visible = false;
                this.onEditorClose.emit(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private addARAdjustment(): void {
        this.loadingVisible = true;
        this.arAdjustmentService.addNewARAdjustment(this.modifiedARAdjustment).subscribe(
            () => {
                this.showSuccessMessage('AR Adjustment added successfully.');
                this.visible = false;
                this.onEditorClose.emit(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateTransactionLog(): void {
        const param: TransactionLogParameterDto = new TransactionLogParameterDto();
        param.transactionId = this.arAdjustmentParam.adjustmentId;
        param.clearCache = true;
        this.loadingVisible = true;
        this.arAdjustmentService.getARAdjustmentHistory(param).subscribe(
            (result: TransactionLogResultDto[]) => {
                this.transactionLogResultData = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private prepareLineItems(): boolean {
        const modifiedLineItems: ARAdjustmentItemRawDto[] = [];
        this.modifiedARAdjustment = new ARAdjustmentRawDto();
        this.modifiedARAdjustment = Object.assign(this.modifiedARAdjustment, this.adjustmentInfo);
        if (this.adjustmentInfo.items && this.adjustmentInfo.items.length > 0) {
            for (const arAdjustmentRawItem of this.adjustmentInfo.items) {
                const dimensionKeysToDelete: string[] = [];
                const dimensionToDelete: any[] = [];
                let rawItem = Object.assign(new ARAdjustmentItemRawDto(), arAdjustmentRawItem);
                if (Utils.equalsIgnoreCase(arAdjustmentRawItem.crudAction, ActionConstants.DELETE)) {
                    rawItem = this.originalAdjusment.items.find((i) =>
                        Utils.equals(i.arAdjustmentItemId, arAdjustmentRawItem.arAdjustmentItemId)
                    );
                    rawItem.dimensions = rawItem.dimensions.filter((d) => Utils.notNullOrEmpty(d.dimensionOptionCode));
                    rawItem.crudAction = ActionConstants.DELETE;
                } else {
                    rawItem.crudAction = !!rawItem.crudAction ? rawItem.crudAction : '';
                    if (Utils.notNullOrEmpty(rawItem.dimensions)) {
                        rawItem.dimensions.forEach((dim) => {
                            const isDimRequired = this.isDimensionMandatory(dim);
                            dim.dimensionOptionCode = rawItem[dim.dimensionCode];
                            dimensionKeysToDelete.push(dim.dimensionCode);

                            if (!isDimRequired && !dim.dimensionOptionCode) {
                                dimensionToDelete.push(dim.dimensionCode);
                            }
                        });

                        dimensionToDelete.forEach((dim) => {
                            const rawIndex = rawItem.dimensions.findIndex((d) =>
                                Utils.equalsIgnoreCase(d.dimensionCode, dim)
                            );
                            if (rawIndex > -1) {
                                rawItem.dimensions.splice(rawIndex);
                            }
                        });
                    }
                    dimensionKeysToDelete.forEach((key) => {
                        delete rawItem[key];
                    });
                    delete rawItem['subCategorySource'];
                    delete rawItem['selectedAccountName'];
                    delete rawItem['disableSubCategory'];
                    delete rawItem['accountType'];
                    delete rawItem['sequenceItemId'];
                }

                modifiedLineItems.push(rawItem);
            }
        }
        this.modifiedARAdjustment.items = modifiedLineItems;
        return true;
    }

    private isDimensionMandatory(dimensionToCheck: DimensionOptionCodeRawDto): boolean {
        const dimension = this.dimensionSource.find((d) =>
            Utils.equalsIgnoreCase(d.code, dimensionToCheck.dimensionCode)
        );
        return dimension.required;
    }

    private resetARAdjustmentRawItem(): void {
        this.prepareDimensions();
        if (this.arAdjustmentEditor.entityUsesSubCategory) {
            this.getSubCategories(false);
        }
    }

    private addRawLineItem(adjInfo?: ARAdjustmentRawDto): void {
        const adjustmentInfo = adjInfo || this.adjustmentInfo;
        const item: ARAdjustmentItemRawDto = new ARAdjustmentItemRawDto();
        item.amount = 0;
        item.openBalance = 0;
        item.arAdjustmentItemId = null;
        item['sequenceItemId'] = Utils.notNullOrEmpty(adjustmentInfo.items)
            ? Math.max.apply(
                  Math,
                  adjustmentInfo.items.map((o) => {
                      return o['sequenceItemId'];
                  })
              ) + 1
            : 1;
        item.crudAction = ActionConstants.ADD;
        item.arAdjustmentId = adjustmentInfo.arAdjustmentId;
        item.description = adjustmentInfo.description;

        item['selectedAccountName'] = '';
        item['disableSubCategory'] = false;
        item['subCategorySource'] = [];
        item['accountType'] = '';
        item['isAccountSubcategoryValid'] = Utils.isTrue(this.arAdjustmentEditor.entityUsesSubCategory) ? false : true;

        adjustmentInfo.items.push(item);

        this.prepareDimensions(adjustmentInfo);
    }

    private prepareEditorLineItems(adjustmentInfo: ARAdjustmentRawDto): void {
        const arAdjustmentItems: ARAdjustmentItemRawDto[] = [];
        let index = 0;
        adjustmentInfo.items.forEach((item) => {
            index += 1;
            let obj: ARAdjustmentItemRawDto = new ARAdjustmentItemRawDto();
            obj = Object.assign(obj, item);
            obj['sequenceItemId'] = index;

            const glAccountSelector = this.glAccountNumberSource.find(
                (d) => d.glAccountNumber === item.glAccountNumber
            );
            if (!!glAccountSelector) {
                obj.glAccountNumber = glAccountSelector.glAccountNumber;
                obj['selectedAccountName'] = `${glAccountSelector.title};${glAccountSelector.glAccountNumber}`;
                this.populateSubcategories(
                    glAccountSelector.glAccountNumber,
                    obj,
                    item.subCategoryCode,
                    'account_number',
                    false,
                    true
                );
            }

            const offsetGlAccountSelector = this.glAccountNumberSource.find(
                (d) => d.glAccountNumber === item.offsetGLAccountNumber
            );
            if (!!offsetGlAccountSelector) {
                obj.offsetGLAccountNumber = offsetGlAccountSelector.glAccountNumber;
            }

            const glAccountLabel = this.glAccountLabelSource.find(
                (d) => !!item.accountLabelCode && Utils.equalsIgnoreCase(d.code, item.accountLabelCode)
            );
            if (!!glAccountLabel) {
                obj.accountLabelCode = glAccountLabel.code;
                obj['selectedAccountName'] = `${glAccountLabel.title};${glAccountLabel.glAccountNumber}`;
                if (!!glAccountLabel.subCategoryCode) {
                    obj.subCategoryCode = glAccountLabel.subCategoryCode;
                    obj['disableSubCategory'] = true;
                    this.populateSubcategories(
                        glAccountLabel.glAccountNumber,
                        obj,
                        item.subCategoryCode,
                        'account_label',
                        false,
                        true
                    );
                }
            }

            arAdjustmentItems.push(obj);
        });

        adjustmentInfo.items = arAdjustmentItems;
        this.prepareDimensions(adjustmentInfo);
    }

    private prepareDimensions(adjInfo?: ARAdjustmentRawDto): void {
        const adjustmentInfo = adjInfo || this.adjustmentInfo;
        if (adjustmentInfo.items && adjustmentInfo.items.length > 0) {
            adjustmentInfo.items.forEach((item) => {
                this.dimensionSource.forEach((dimNode) => {
                    let itemDimension: DimensionOptionCodeRawDto;

                    if (item.dimensions && item.dimensions.length > 0) {
                        itemDimension = item.dimensions.find((d) =>
                            Utils.equalsIgnoreCase(d.dimensionCode, dimNode.code)
                        );
                    }
                    if (itemDimension) {
                        if (itemDimension.dimensionOptionCode) {
                            const selectedNode = dimNode.options.find((d) =>
                                Utils.equalsIgnoreCase(d.code, itemDimension.dimensionOptionCode)
                            );
                            item[itemDimension.dimensionCode] = selectedNode ? selectedNode.code : null;
                        } else if (!item[itemDimension.dimensionCode]) {
                            item[itemDimension.dimensionCode] = null;
                        }
                    } else {
                        const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                        obj.dimensionCode = dimNode.code;
                        obj.dimensionOptionCode = null;
                        item[dimNode.code] = null;
                        if (Utils.isNullOrUndefined(item.dimensions)) {
                            item.dimensions = [];
                        }
                        item.dimensions.push(obj);
                    }
                });
            });
        }
    }

    private populateARadjustmentEditor(): void {
        const param: ARAdjustmentEditorParameterDto = new ARAdjustmentEditorParameterDto();
        param.communityId = this.arAdjustmentParam.communityId;
        param.managementCompanyId = this.arAdjustmentParam.managementCompanyId;
        param.adjustmentId = !!this.arAdjustmentParam.adjustmentId ? this.arAdjustmentParam.adjustmentId : null;

        if (Utils.notNullAndDefined(this.customerId)) {
            param.customerId = this.customerId;
        } else if (Utils.notNullAndDefined(this.customerCode)) {
            // Input coming from Property Context => Statements
            param.customerCode = this.customerCode;
        }

        this.loadingVisible = true;
        this.arAdjustmentService.getARAdjustmentEditor(param).subscribe(
            (result: ARAdjustmentEditorResultDto) => {
                this.glAccountNumberSource = result.glAccounts;
                this.arAdjustmentEditor = result;
                this.prepareAttachments();
                if (Utils.isNullOrEmpty(result.adjustmentInfo.arAdjustmentId)) {
                    this.arAdjustmentEditor.allowFullEdit = true;
                    this.arAdjustmentEditor.allowOtherDimensionEdit = true;
                }
                this.adjustmentInfo = result.adjustmentInfo;
                if (
                    Utils.notValidDtoId(this.adjustmentInfo.subLedgerId) &&
                    Utils.notNullOrEmpty(this.arAdjustmentEditor.subLedgers)
                ) {
                    let defaultSubLedger = this.arAdjustmentEditor.subLedgers.find((d) => Utils.isTrue(d.isDefault));
                    if (Utils.isNullOrUndefined(defaultSubLedger)) {
                        defaultSubLedger = this.arAdjustmentEditor.subLedgers[0];
                    }

                    this.adjustmentInfo.subLedgerId = defaultSubLedger.key;
                    this.populateGLAccountsLabel(this.adjustmentInfo.subLedgerId);
                } else {
                    this.prepareAdjustmentEditor();
                }

                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.serverErrorService.displayError(error);
            }
        );
    }

    private prepareAdjustmentEditor(): void {
        this.adjustmentTypes = this.arAdjustmentEditor.adjustmentTypes;
        if (!this.adjustmentInfo.adjustmentTypeCode) {
            this.adjustmentTypes.forEach((type) => {
                if (type.isDefault) {
                    this.adjustmentInfo.adjustmentTypeCode = type.key;
                }
            });
        }
        this.subLedgers = this.arAdjustmentEditor.subLedgers;
        this.dimensionSource = this.arAdjustmentEditor.glDimensions;
        this.dimensionSource.forEach((dim) => {
            dim.isEditable = this.isDimensionEditable(dim);
        });
        if (!this.isEdited && Utils.isNullOrEmpty(this.adjustmentInfo.items)) {
            this.adjustmentInfo.docDate = new Date();
            this.adjustmentInfo.postDate = new Date();
            this.addRawLineItem(this.arAdjustmentEditor.adjustmentInfo);
        } else {
            this.prepareEditorLineItems(this.arAdjustmentEditor.adjustmentInfo);
        }

        if (this.arAdjustmentEditor.entityUsesSubCategory) {
            this.getSubCategories(false); // each line item will have subcategories populated.
        }
        this.originalAdjusment = cloneDeep(this.adjustmentInfo);
    }

    private isDimensionEditable(dimension: DimensionSelectorResultDto): boolean {
        let isEditable = !this.isLimitedEdit;
        if (Utils.isFalse(dimension.required)) {
            isEditable = this.arAdjustmentEditor.allowOtherDimensionEdit && !this.isLimitedEdit;
        }

        return isEditable;
    }

    private configureAccountTypeSource(): void {
        this.accountTypeSource = [
            {
                name: 'account_label',
                text: 'Account Label'
            },
            {
                name: 'account_number',
                text: 'Account Number'
            }
        ];
    }

    private getSubCategories(showWarning: boolean): void {
        if (this.adjustmentInfo.items && this.adjustmentInfo.items.length > 0) {
            this.adjustmentInfo.items.forEach((item) => {
                const accountType = Utils.notNullOrEmpty(item.accountLabelCode) ? 'account_label' : 'account_number';
                if (item.glAccountNumber) {
                    this.populateSubcategories(
                        item.glAccountNumber,
                        item,
                        item.subCategoryCode,
                        accountType,
                        false,
                        showWarning
                    );
                }
            });
        }
    }

    private populateSubcategories(
        glAccountNumber: string,
        rawItem: any,
        selectedSubcategoryCode: string = null,
        selectedAccountType,
        clearCache = false,
        showInformation: boolean
    ): void {
        const param: SubCategoryByGlAccountParametersDto = new SubCategoryByGlAccountParametersDto();
        param.managementCompanyId = this.arAdjustmentParam.managementCompanyId;
        param.communityId = this.arAdjustmentParam.communityId;
        param.glAccountNumber = glAccountNumber;
        param.clearCache = clearCache;

        this.arAdjustmentService.getSubCategories(param).subscribe(
            (result: GLSubcategorySelectorResultDto[]) => {
                if (Utils.isTrue(this.arAdjustmentEditor.entityUsesSubCategory)) {
                    const { rawItem: updatedRawItem, infoMessage } = this.cirabooksHelperService.processSubcategories(
                        rawItem,
                        result,
                        selectedSubcategoryCode,
                        selectedAccountType,
                        this.glAccountLabelSource,
                        this.glAccountNumberSource
                    );

                    // Replace the original rawItem with the updated one
                    rawItem = updatedRawItem;

                    if (Utils.isTrue(showInformation) && Utils.notNullOrEmpty(infoMessage)) {
                        const dialog = this.helperService.getOkDialog('Information', infoMessage);
                        dialog.show();
                    }
                }
            },
            (error) => {
                this.serverErrorService.displayError(error);
            }
        );
    }

    private populateGLAccountsLabel(subLedgerId: number): void {
        this.glAccountLabelSource = [];
        const param: ARAdjustmentGLAccountLabelsParameterDto = new ARAdjustmentGLAccountLabelsParameterDto();
        param.communityId = this.arAdjustmentParam.communityId;
        param.managementCompanyId = this.arAdjustmentParam.managementCompanyId;
        param.subLedgerId = subLedgerId;
        this.arAdjustmentService.getARAdjustmentGLAccountLabels(param).subscribe(
            (result) => {
                this.glAccountLabelSource = result;
                this.prepareAdjustmentEditor();
            },
            (error) => {
                this.serverErrorService.displayError(error);
            }
        );
    }

    private checkLineItemValidity(): any {
        const resultStatus = { isValid: true, message: null };

        this.adjustmentInfo.items.forEach((data, index) => {
            if (Utils.notEqualsIgnoreCase(data.crudAction, ActionConstants.DELETE)) {
                const itemIndex = index + 1;
                if (Utils.isNullOrUndefined(data.glAccountNumber)) {
                    resultStatus.isValid = false;
                    resultStatus.message = `The GL Account number is Required for ItemId #${itemIndex}`;
                } else if (
                    Utils.isFalse(data.isAccountSubcategoryValid) &&
                    Utils.notNullAndDefined(data.accountSubcategoryValidationMessage)
                ) {
                    resultStatus.isValid = false;
                    resultStatus.message = `Invalid Line Item as ${data.accountSubcategoryValidationMessage}`;
                } else if (data.amount <= 0) {
                    resultStatus.isValid = false;
                    resultStatus.message = `Line Item Amount must not equal to Zero ItemId #${itemIndex}`;
                }

                this.dimensionSource.forEach((dimension) => {
                    if (dimension.required) {
                        const itemDimension = data.dimensions.find((d) =>
                            Utils.equalsIgnoreCase(d.dimensionCode, dimension.code)
                        );
                        if (!!itemDimension && !itemDimension.dimensionOptionCode) {
                            resultStatus.isValid = false;
                            resultStatus.message = `${dimension.code} is Required for ItemId #${itemIndex}`;
                        }
                    }
                });
            } else {
                // if crudAction is delete, let's pass the lineitem from validation
                data.isAccountSubcategoryValid = true;
                data.accountSubcategoryValidationMessage = null;
            }
        });
        return resultStatus;
    }

    private prepareAttachments(): void {
        this.arAdjustmentAttachments = [];
        if (Utils.notNullOrEmpty(this.arAdjustmentEditor.adjustmentInfo.attachments)) {
            this.arAdjustmentEditor.adjustmentInfo.attachments.forEach((attachment) => {
                const obj: AttachmentRawDto = new AttachmentRawDto();
                const downloadUrl =
                    URLConstants.ccCBARAdjustmentApiUrl +
                    `/attachment?managementCompanyId=${this.arAdjustmentParam.managementCompanyId}&communityId=${this.arAdjustmentParam.communityId}&adjustmentId=${this.arAdjustmentParam.adjustmentId}&attachmentId=${attachment.fileId}`;
                obj.fileId = attachment.fileId;
                obj.fileBase64String = attachment.fileDataBase64;
                obj.fileName = attachment.fileName;
                obj.action = attachment.crudAction;
                obj.fileUrl = downloadUrl;
                this.arAdjustmentAttachments.push(obj);
            });
        }
    }
}
